import React, { useEffect, useRef, useState } from 'react';
import { X, Users } from 'lucide-react';

const COLORS = [
  '#6a1fbf', '#00c853', '#2196f3', '#ff9800', '#e91e63',
  '#009688', '#ff5722', '#3f51b5', '#8bc34a', '#f44336',
  '#00bcd4', '#cddc39', '#9c27b0', '#ff6f00', '#1b5e20',
];

const ROLE_STYLES = {
  host:      { label: 'Host',     bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  moderator: { label: 'Host',     bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  'co-host': { label: 'Co-Host',  bg: 'bg-blue-500/20',   text: 'text-blue-400',   border: 'border-blue-500/30' },
  attendee:  { label: 'Attendee', bg: 'bg-green-500/20',  text: 'text-green-400',  border: 'border-green-500/30' },
};

function normalizeParticipant(p) {
  if (typeof p === 'string') return { name: p, role: null };
  return { name: p.name || 'Unknown', role: p.role || null };
}

function getColorFromName(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLORS[Math.abs(hash) % COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ParticipantsPopup = ({ participants, onClose, anchorRef }) => {
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef?.current || !popupRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let top = anchorRect.bottom + 8;
    let left = anchorRect.left;

    // Keep within viewport horizontally
    if (left + popupRect.width > viewportW - 16) {
      left = viewportW - popupRect.width - 16;
    }
    if (left < 16) left = 16;

    // If it would go below viewport, show above
    if (top + popupRect.height > viewportH - 16) {
      top = anchorRect.top - popupRect.height - 8;
    }

    setPosition({ top, left });
  }, [anchorRef]);

  if (!participants || participants.length === 0) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        ref={popupRef}
        className="fixed z-[70] w-72 bg-[#0d1b3e] border border-white/15 rounded-xl shadow-2xl overflow-hidden"
        style={{ top: position.top, left: position.left }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#00c853]" />
            <span className="text-sm font-semibold text-white">
              Participants ({participants.length})
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5 text-white/50" />
          </button>
        </div>

        {/* List */}
        <div className="max-h-64 overflow-y-auto p-2">
          {participants.map((p, idx) => {
            const { name, role } = normalizeParticipant(p);
            const color = getColorFromName(name);
            const roleStyle = role ? ROLE_STYLES[role] || ROLE_STYLES.attendee : null;
            return (
              <div
                key={idx}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {getInitials(name)}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-white/90 truncate">{name}</span>
                  {roleStyle && (
                    <span className={`text-[10px] ${roleStyle.text} mt-0.5`}>
                      {roleStyle.label}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ParticipantsPopup;
